import { REVIEWS } from './reviews.js';

// Free PT session campaign (/free-pt-session). Runs go month on, month off,
// with different trainers each run. Switching a run on or off is a change to
// this list only: the page, the homepage card and the My PODs card all read it.
//
// The current run is the first one that hasn't ended. Before its `start` the
// page is "upcoming" with the form open, so add the next run only when its
// form should open. Between runs the page shows its "back soon" state.
// `promoteFrom` to `end` is when the homepage and My PODs cards show.
// `listingId` is the trainer's _id in GET /pt/listings/all (photo and bio come from there).
// `slots` are the trainer's windows: `day` ("Tuesday mornings", or a date, "Saturday 21 November"),
// `short` ("Tue", for a promo tile with two or more windows) and `hours` ("8am to 12pm").
// A weekly window has `weekday` ("Tuesday"): its card lists the dates left, from today (or `from`,
// if later) to `until` (end of its last day), else the run's end; `dated` puts them in the day
// instead ("Saturday 7 and 14 November"). A one-off window names its date in `day` and ends at `until`.
// A window with nothing left drops off; a trainer with no windows left drops off the page, the form
// and the promo card.
// `promoDay` overrides the promo tile's day ("Sat 21 Nov"); `isNew` adds a "New" tag to the card.
// `cardPhoto` replaces the listing photo on the card and form tile (a square crop in public/img/free-pt/).
// Photos sit in a round frame: `photoPosition` (object-position) picks the crop,
// then the photo is scaled by `photoZoom` (default 1.3) around `photoFocus`
// (transform-origin, roughly where the face is). Tune both per photo.
// `bio` is page-only (Find a Pro and the API keep the originals): blocks of
// { p } paragraph, { h } small heading, { ul } bullet list. Without one, the
// card falls back to the listing's description from the API.
// `review` is a Google review from src/data/reviews.js, cut with reviewExcerpt().
// `introPrice` is the usual PT Intro price in pounds, struck through on the page and card.
// `promoPhoto` is the trainer's tile on the promo card (FreePtPromo), promoTile('<file name>'):
// each trainer in the next run needs one 320×400 face-framed JPEG in src/assets/free-pt/,
// and the build (scripts/optimize-images.js) makes its AVIF and WebP at 320, 240 and 160 wide.
// Without one, the tile shows the trainer's initial.

// Sentences of a review in reviews.js, in the original wording. `parts` lists the
// sentences to keep by index, as [index] or [index, words] to keep only its first
// `words` words. An ellipsis marks each cut: a shortened sentence, or a gap.
export function reviewExcerpt(location, id, parts) {
  const review = REVIEWS[location].items.find((r) => r.id === id);
  const sentences = review.text.match(/[^.!?]+[.!?]+/g).map((x) => x.trim());
  const quote = parts.map(([index, words], i) => {
    const next = parts[i + 1];
    const text = words ? sentences[index].split(' ').slice(0, words).join(' ') : sentences[index];
    const cut = !!words || (!!next && next[0] !== index + 1);
    return cut ? `${text}…` : text;
  }).join(' ');
  return { quote, name: review.author, date: review.date, rating: review.rating };
}

// The promo tiles' files: each crop's JPEG, plus the AVIF and WebP at 160, 240 and 320
// wide the build makes from it (putney-2026-10-giorgio-240.avif ...). Nothing else.
const FREE_PT_FILES = require.context('@/assets/free-pt', false, /^\.\/[a-z0-9-]+(\.jpg|-(160|240|320)\.(avif|webp))$/);

// A promo tile's image set, { src, avif, webp }: the JPEG fallback and a srcset per format
// ("<url> 160w, <url> 240w, <url> 320w"), the shape the pro directory's images use.
function promoTile(name) {
  const srcset = (ext) => FREE_PT_FILES.keys()
    .map((key) => key.match(new RegExp(`^\\./${name}-(\\d+)\\.${ext}$`)))
    .filter(Boolean)
    .sort((a, b) => a[1] - b[1])
    .map(([key, width]) => `${FREE_PT_FILES(key)} ${width}w`)
    .join(', ');
  return { src: FREE_PT_FILES(`./${name}.jpg`), avif: srcset('avif'), webp: srcset('webp') };
}

export const FREE_PT_RUNS = [
  {
    id: 'putney-2026-10',
    location: 'Putney',
    promoteFrom: '2026-10-23T07:00:00Z', // Fri 23 Oct, 8am BST: homepage and My PODs links appear
    start: '2026-10-26T00:00:00Z',       // Mon 26 Oct, midnight (clocks go back on 25 Oct)
    end: '2026-11-28T23:59:59Z',         // Sat 28 Nov, end of day
    introPrice: 35,
    // David K., May 2026 (also on /putney), cut so it names no one trainer.
    review: { ...reviewExcerpt('putney', 'putney-011', [[0, 10], [2], [3]]), source: 'Google review' },
    trainers: [
      { key: 'rosemary', listingId: '6ac509de3a90ded872153486', name: 'Rosemary', role: 'Personal Trainer', pronoun: 'her', isNew: true,
        hook: 'Strength coach with a biomedical science background. Lower-back resilience and strength for injury-prone and desk-bound bodies.',
        slots: [{ day: 'Saturday 21 November', short: 'Sat', hours: '9am to 1pm', until: '2026-11-21T23:59:59Z' }],
        promoDay: 'Sat 21 Nov',
        // Her listing photo is full length (360x640), so the card uses a square crop of it: head and
        // shoulders and raised arms, face in the upper middle (200x200 from x 86, y 150). The member confirmation uses it too.
        cardPhoto: '/img/free-pt/putney-2026-10-rosemary-square.jpg',
        photoPosition: 'center', photoZoom: 1, photoFocus: 'center', promoPhoto: promoTile('putney-2026-10-rosemary'),
        bio: [
          { p: "Introducing Rosemary, a strength coach and the founder of Intentional Strength. With a background in biomedical science and years of coaching experience, she specialises in lower-back resilience, and in training complex, injury-prone and desk-bound bodies." },
          { p: "Her philosophy is simple: strength training as a practice of presence. Rosemary coaches the whole person: not just the hour in the gym, but how you move, recover and feel, in your body and your life." },
          { p: "She helps high achievers get out of their heads and back into their bodies, building strength, resilience and the confidence to feel at home in themselves, for life." },
          { p: "Whether you're rebuilding after injury, managing a demanding desk-bound life, or wanting to move and feel genuinely strong, Rosemary meets you where you are, with a thoughtful, whole-person, nervous-system-aware approach tailored to you." },
        ],
      },
      { key: 'anna', listingId: '6aac114f6da7c1c2045e292e', name: 'Anna', role: 'Personal Trainer', pronoun: 'her', isNew: true,
        hook: "Strength and technique-focused coaching, whether you're new to lifting, coming back after time away or training around an injury.",
        slots: [{ day: 'Saturday 28 November', short: 'Sat', hours: '9am to 1pm', until: '2026-11-28T23:59:59Z' }],
        promoDay: 'Sat 28 Nov',
        photoPosition: 'center', photoZoom: 1.6, photoFocus: '50% 30%', promoPhoto: promoTile('putney-2026-10-anna'),
        bio: [
          { p: "Hi, I'm Anna, a Personal Trainer based at GYMPODS Putney, specialising in strength training and technique-focused coaching." },
          { p: "I help people build strength, move well and feel more confident in the gym. My approach is simple, structured and tailored to each person, whether you're new to strength training, returning to exercise after time away, or looking to take your training to the next level." },
          { h: "How I can help" },
          { ul: [
            "Fat loss: sustainable progress through structured training and realistic habits",
            "Strength training: progressive, technique-focused programming to build strength and confidence",
            "Form and technique: learning to move safely, efficiently and with good technique",
            "Mobility and stability: better movement quality, control and range of motion",
            "Training around injuries: exercises adapted to your needs, limitations or previous injuries",
            "Gym confidence: learning to use the equipment and train independently",
            "Programming and accountability: clear structure, progression and support to help you stay consistent",
          ] },
        ],
      },
      { key: 'giorgio', listingId: '6a1826454ac11e4bba535c76', name: 'Giorgio', role: 'Personal Trainer', pronoun: 'his',
        hook: 'Sports scientist and nutrition coach. Strength, body recomposition and running, with a plan that fits your life, not a list of food rules.',
        slots: [
          { day: 'Tuesday mornings', weekday: 'Tuesday', short: 'Tue', hours: '8am to 12pm' },
          { day: 'Friday lunchtimes', weekday: 'Friday', short: 'Fri', hours: '10:30am to 2pm', from: '2026-11-06T00:00:00Z' }, // Fri 6 Nov to Fri 27 Nov
        ],
        photoPosition: 'center 0%', photoZoom: 1.6, photoFocus: '72% 24%', promoPhoto: promoTile('putney-2026-10-giorgio'),
        bio: [
          { p: "My name is Giorgio, but you can call me G." },
          { p: "I've always been an athlete, with broad experience of competitive sport and my best results in football, swimming and running. Over the years, I've coached both athletes and the general fitness population." },
          { p: "In my teens, I struggled with an eating disorder. I couldn't find someone who truly saw me, listened to me and helped guide me out of that dark place. That experience led me to a BSc in Sports Science, followed by an MSc in Sports Science, Rehab and Lifestyle Change. I'm now completing a second MSc, in Human Nutrition, all so I can help others find peace in their bodies, enjoy movement and live their lives fully." },
          { p: "I know how painful it can be to look at your body and feel ashamed, or to be caught in a cycle of endless diets and food rules. I've been there too. Today, I coach people who want a safe space to reach their fitness goals without giving up the things that truly matter to them." },
          { p: "Let me be the coach you always hoped for." },
          { h: "Qualifications" },
          { ul: [
            "BSc Sports Science",
            "MSc Sports Science, Rehab and Lifestyle Change",
            "MSc Human and Clinical Nutrition (in progress)",
            "Level 6 PT certification",
            "Functional Movement Screen (FMS), Levels 1 and 2",
            "FCS Level 1",
            "Hybrid Training Specialist",
            "Precision Nutrition Level 1",
            "Level 2 Counselling",
            "Level 1 Mental Health First Aid",
            "EXOS Performance (in progress)",
            "Endurance Nutrition Specialist (in progress)",
            "Supplements Specialist (in progress)",
          ] },
        ],
      },
      { key: 'shannon', listingId: '691aeda02e2e312b4652aa74', name: 'Shannon', role: 'Personal Trainer', pronoun: 'his',
        hook: "Over 15 years in some of London's best gyms. Fat loss, strength, mobility and posture.",
        slots: [{ day: 'Wednesday evenings', weekday: 'Wednesday', short: 'Wed', hours: '4pm to 8pm' }],
        photoPosition: 'center 28%', photoZoom: 1.6, photoFocus: 'center 44%', promoPhoto: promoTile('putney-2026-10-shannon'),
        bio: [
          { p: "I'm a fitness trainer and lifestyle coach, with a degree in Human Movement and Health Education from the University of Sydney." },
          { p: "I've worked in London's best fitness establishments for over 15 years, and I always stay at the top of my game." },
          { p: "My mission is a bold one: to help as many people as I can reach their ultimate state of health, fitness and wellbeing. If you want to level up physically and mentally, Shannon is your man." },
          { h: "How I can help" },
          { p: "I have a vast knowledge of fitness, health and wellness, and can help you with:" },
          { ul: [
            "Fat loss",
            "Body reshaping",
            "Fitness and strength training",
            "Muscle gain and toning",
            "Mobility",
            "Postural correction",
            "Pre- and post-natal care",
          ] },
          { p: "I look forward to meeting you and working with you soon." },
        ],
      },
      { key: 'matilda', listingId: '6ace376a706a197f0ad670c4', name: 'Matilda', role: 'Personal Trainer', pronoun: 'her',
        hook: 'Former international rower. Simple, fun, no-frills strength training, and a great fit if the gym feels intimidating.',
        // Sat 7 and 14 Nov. Her claim emails go to bookpt@ only (backend copyTrainer: false).
        // `dated`: her card and promo tile label read "Saturday 7 and 14 November", her tile "Sat 7 & 14 Nov";
        // from 8 Nov, "Saturday 14 November" and "Sat 14 Nov".
        slots: [{ day: 'Saturday mornings', weekday: 'Saturday', dated: true, short: 'Sat', hours: '9am to 1pm', until: '2026-11-14T23:59:59Z' }],
        photoPosition: 'center 37%', photoZoom: 1.6, photoFocus: '45% 52%', promoPhoto: promoTile('putney-2026-10-matilda'),
        bio: [
          { p: "I'm Matilda, a Level 3 qualified Personal Trainer." },
          { p: "My main focus is helping women of all ages get past gym intimidation and that feeling of not knowing where to start, although I love working with everyone!" },
          { p: "Training with me is simple, fun and to the point. I believe in no-frills, effective training that saves you time and energy. I bring friendliness and fun to every session, helping you build strength and confidence without spending hours in the gym or getting caught up in complicated nonsense." },
          { h: "How I can help you" },
          { ul: [
            "Build muscle and change your physique",
            "Get stronger and fitter",
            "Master your form and technique, so you feel confident training alone",
            "Follow a personalised training plan tailored to your goals",
          ] },
          { p: "I was a high-level rower for 11 years, competing nationally and internationally and earning various titles and medals. I bring that elite sport experience into every session. Strength training was a huge part of my rowing career, and I believe it changed my life. I know it has the power to change yours, too." },
        ],
      },
    ],
  },
];

// Trainer counts in copy: NUMBER_WORDS[3] → "Three".
export const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'];

const ms = (iso) => Date.parse(iso);

export function currentRun(now = Date.now()) {
  return FREE_PT_RUNS.find((run) => now <= ms(run.end)) || null;
}

// 'upcoming' (before start), 'running' (until end) or 'off' (no run).
export function campaignState(now = Date.now()) {
  const run = currentRun(now);
  if (!run) return 'off';
  return now < ms(run.start) ? 'upcoming' : 'running';
}

export function isPromoted(now = Date.now()) {
  const run = currentRun(now);
  return !!run && now >= ms(run.promoteFrom) && now <= ms(run.end);
}

// Whether a slot has started: one without `from` runs for the whole run.
export function slotStarted(slot, now = Date.now()) {
  return !slot.from || now >= ms(slot.from);
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_MS = 86400000;

// An instant's UK calendar date, as that date's UTC midnight, so whole days can be added.
function ukDay(t) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric' })
    .formatToParts(new Date(t)).map((p) => [p.type, p.value]));
  return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
}

// A weekly window's dates left, today included, as UTC midnights: each `weekday` from today (or its
// `from`, if later) to its `until`, else the run's end. [] for a one-off window.
export function slotDates(slot, run, now = Date.now()) {
  if (!slot.weekday) return [];
  const last = ukDay(slot.until || run.end);
  let day = ukDay(Math.max(now, slot.from ? ms(slot.from) : 0));
  day += ((WEEKDAYS.indexOf(slot.weekday) - new Date(day).getUTCDay() + 7) % 7) * DAY_MS;
  const dates = [];
  for (; day <= last; day += 7 * DAY_MS) dates.push(day);
  return dates;
}

// Dates as "6, 13, 20 and 27 November", by month across a month end: "29 December, 5 and 12 January".
// datesText(dates, { month: 'short', and: '&' }) gives "7 & 14 Nov", for a promo tile.
export function datesText(dates, { month: monthStyle = 'long', and: andWord = 'and' } = {}) {
  const months = [];
  for (const d of dates) {
    const date = new Date(d);
    const month = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: monthStyle }).format(date);
    const last = months[months.length - 1];
    if (last && last.month === month) last.days.push(date.getUTCDate());
    else months.push({ month, days: [date.getUTCDate()] });
  }
  const and = (xs) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} ${andWord} ${xs[xs.length - 1]}` : String(xs[0]));
  return months.map((m) => `${and(m.days)} ${m.month}`).join(', ');
}

// Whether a window has anything left: a weekly one, a date to come; a one-off one, its `until` not passed.
export function slotOpen(slot, run, now = Date.now()) {
  return slot.weekday ? slotDates(slot, run, now).length > 0 : !slot.until || now <= ms(slot.until);
}

// The run's trainers who can still be claimed, in config order, each with only their open windows.
// Each window gets its `dates` left and the card's `label`: `day`, or for a `dated` one "Saturday 7 and 14 November".
export function claimableTrainers(run, now = Date.now()) {
  return run.trainers
    .map((t) => ({
      ...t,
      slots: t.slots.filter((slot) => slotOpen(slot, run, now)).map((slot) => {
        const dates = slotDates(slot, run, now);
        return { ...slot, dates, label: slot.dated ? `${slot.weekday} ${datesText(dates)}` : slot.day };
      }),
    }))
    .filter((t) => t.slots.length > 0);
}

const pad = (n) => String(n).padStart(2, '0');

// A run's countdown, shared by the landing page and the promo card so they can't disagree:
// to `start` before the run starts ("Sessions start in"), then to `end` ("Sessions end in").
// `left` is whole seconds (never below 0), `parts` the zero-padded split
// ({ days: '31', hours: '05', mins: '59', secs: '12' }) and `compact` "31d 05h 59m 12s".
export function countdown(run, now = Date.now()) {
  const upcoming = now < ms(run.start);
  const target = upcoming ? run.start : run.end;
  const left = Math.max(0, Math.floor((ms(target) - now) / 1000));
  const parts = {
    days: pad(Math.floor(left / 86400)),
    hours: pad(Math.floor((left % 86400) / 3600)),
    mins: pad(Math.floor((left % 3600) / 60)),
    secs: pad(left % 60),
  };
  return {
    upcoming,
    target,
    label: upcoming ? 'Sessions start in' : 'Sessions end in',
    left,
    parts,
    compact: `${parts.days}d ${parts.hours}h ${parts.mins}m ${parts.secs}s`,
  };
}

// UK dates for copy: ukDate(iso, { day: 'numeric', month: 'short' }) → "26 Oct".
export function ukDate(iso, options) {
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', ...options }).format(new Date(iso));
}
